import {produce} from 'immer';
import {useAtom, useSetAtom} from 'jotai';
import {uniqueId} from 'lodash';
import {memo, useEffect} from 'react';
import {AnalysisItem} from '@/map/@types';
import {analysisDrawItemAtom, anaysisListAtom} from '@/map/atom';
import {MAX_VISIBLE_ZOOM_METER} from '@/map/constants';
import {AreaOverlayType} from '@/map/enums';
import {useVisibleZoomLevel} from '@/map/hooks';
import {CircleDrawingLayer} from './Circle';
import {SelectEmdLayer} from './Emd';
import {PolygonDrawingLayer} from './Polygon';

interface IProps {
  map: naver.maps.Map;
}

export const DrawingLayer = memo((props: IProps) => {
  const [drawingItem, setDrawItem] = useAtom(analysisDrawItemAtom);

  useEffect(() => {
    return () => {
      setDrawItem(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (drawingItem) {
    return <Layer {...props} item={drawingItem} />;
  }

  return null;
});

const Layer = ({map, item}: IProps & {item: AnalysisItem}) => {
  const setList = useSetAtom(anaysisListAtom);
  const setDrawItem = useSetAtom(analysisDrawItemAtom);
  const isActiveZoom = useVisibleZoomLevel(MAX_VISIBLE_ZOOM_METER.anaysis);

  const finishDrawingMode = () => setDrawItem(null);

  const updateOverlay = (
    next:
      | AnalysisItem[AreaOverlayType.CIRCLE]
      | AnalysisItem[AreaOverlayType.POLYGON]
      | AnalysisItem[AreaOverlayType.EMD]
  ) => {
    finishDrawingMode();

    const type = item.type;
    const id = uniqueId(`${type}-`);

    setList(
      produce((prev) => {
        prev.push({
          type,
          id,
          [type]: next,
        });
      })
    );
  };

  if (item.type === AreaOverlayType.CIRCLE) {
    return (
      <CircleDrawingLayer
        map={map}
        item={item[AreaOverlayType.CIRCLE]}
        isActiveZoomLevel={isActiveZoom}
        onCancel={finishDrawingMode}
        onComplete={(next) => updateOverlay(next)}
      />
    );
  } else if (item.type === AreaOverlayType.POLYGON) {
    return (
      <PolygonDrawingLayer
        map={map}
        item={item[AreaOverlayType.POLYGON]}
        isActiveZoomLevel={isActiveZoom}
        onCancel={finishDrawingMode}
        onComplete={(next) => updateOverlay(next)}
      />
    );
  } else if (item.type === AreaOverlayType.EMD) {
    return (
      <SelectEmdLayer
        map={map}
        isActiveZoomLevel={isActiveZoom}
        onCancel={finishDrawingMode}
        onComplete={(next) => updateOverlay(next)}
      />
    );
  }

  return null;
};
